import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Zap, Bell, RefreshCw, Send, Users, FileText, CheckCircle, AlertTriangle } from 'lucide-react';

import { authHeaders } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminAutomations() {
  const [scenarios, setScenarios] = useState([]);
  const [toReactivate, setToReactivate] = useState([]);
  const [toFollowup, setToFollowup] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('scenarios');
  const [sendingReactivation, setSendingReactivation] = useState(null);
  const [orderId, setOrderId] = useState('');
  const [postLoading, setPostLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND}/api/automations/scenarios`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${BACKEND}/api/automations/clients-a-relancer`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${BACKEND}/api/automations/devis-a-relancer`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${BACKEND}/api/automations/logs`, { headers: authHeaders() }).then(r => r.json()),
    ]).then(([sc, cr, dr, lg]) => {
      setScenarios(sc.scenarios || []);
      setToReactivate(cr.clients_a_relancer || []);
      setToFollowup(dr.devis_a_relancer || []);
      setLogs(lg.logs || []);
    }).catch((err) => { /* eslint-disable-line no-empty */ }).finally(() => setLoading(false));
  }, []);

  async function toggleScenario(id) {
    const res = await fetch(`${BACKEND}/api/automations/scenarios/${id}/toggle`, { method: 'POST', headers: authHeaders() });
    const data = await res.json();
    setScenarios(prev => prev.map(s => s.id === id ? { ...s, actif: data.actif } : s));
    toast.success(`Scénario ${data.actif ? 'activé' : 'désactivé'}`);
  }

  async function sendReactivation(clientId) {
    setSendingReactivation(clientId);
    const res = await fetch(`${BACKEND}/api/automations/send-reactivation/${clientId}`, { method: 'POST', headers: authHeaders() });
    const data = await res.json();
    toast.success(`Email envoyé (${data.result?.simulated ? 'simulé' : 'réel'})`);
    setSendingReactivation(null);
  }

  async function sendPostPrestation() {
    if (!orderId) { toast.error('ID commande requis'); return; }
    setPostLoading(true);
    const res = await fetch(`${BACKEND}/api/automations/send-post-prestation/${orderId}`, { method: 'POST', headers: authHeaders() });
    const data = await res.json();
    toast.success('Email post-prestation envoyé !');
    setPostLoading(false);
    fetchLogs();
  }

  async function fetchLogs() {
    const res = await fetch(`${BACKEND}/api/automations/logs`, { headers: authHeaders() });
    const data = await res.json();
    setLogs(data.logs || []);
  }

  const TABS = [
    { id: 'scenarios', label: 'Scénarios' },
    { id: 'relances', label: `Relances (${toReactivate.length + toFollowup.length})` },
    { id: 'logs', label: 'Journaux' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Automatisations</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Automatisations</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Scenarios actifs et logs</p>
      </div>

      <div className="mb-6">
        <h2 className="font-display text-xl font-bold" style={{ color: 'var(--admin-text)' }}>Automatisations</h2>
        <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Scénarios, relances, réactivation clients, post-prestation</p>
      </div>

      <div className="flex gap-1 mb-6 p-1 rounded-xl" style={{ background: 'var(--admin-surface)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className="flex-1 py-2 rounded-lg text-xs font-medium transition-all"
            style={{ background: activeTab === t.id ? '#D4AF37' : 'transparent', color: activeTab === t.id ? '#0D0D0D' : 'rgba(255,255,255,0.5)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin w-8 h-8 rounded-full" style={{ border: '2px solid #2A2A2A', borderTopColor: '#D4AF37' }} /></div>
      ) : (
        <>
          {/* SCENARIOS */}
          {activeTab === 'scenarios' && (
            <div className="space-y-3">
              {/* Post-prestation manual trigger */}
              <div className="p-5 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid rgba(212,175,55,0.2)' }}>
                <h3 className="font-semibold text-sm mb-3" style={{ color: 'var(--admin-gold)' }}>Déclencher manuellement</h3>
                <div className="flex gap-3">
                  <input type="text" value={orderId} onChange={e => setOrderId(e.target.value)}
                    placeholder="ID commande"
                    className="flex-1 px-3 py-2 rounded-xl text-sm" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }} />
                  <button onClick={sendPostPrestation} disabled={postLoading}
                    className="btn-gold px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-1 disabled:opacity-40">
                    <Send size={14} /> Post-prestation
                  </button>
                </div>
              </div>

              {/* Scenario cards */}
              {scenarios.map(s => (
                <div key={s.id} className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: s.actif ? 'rgba(61,220,151,0.12)' : 'rgba(207,207,207,0.08)', color: s.actif ? '#3DDC97' : '#CFCFCF' }}>
                        <Zap size={16} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>{s.nom}</p>
                        <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{s.description}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleScenario(s.id)}
                      className="px-3 py-1.5 rounded-xl text-xs font-semibold"
                      style={{
                        background: s.actif ? 'rgba(61,220,151,0.12)' : 'rgba(207,207,207,0.1)',
                        color: s.actif ? '#3DDC97' : '#CFCFCF',
                        border: `1px solid ${s.actif ? 'rgba(61,220,151,0.2)' : '#2A2A2A'}`
                      }}>
                      {s.actif ? '● Actif' : '○ Inactif'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* RELANCES */}
          {activeTab === 'relances' && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Devis non signés */}
              <div className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: '#FFCC66' }}>
                  <FileText size={14} /> Devis envoyés, non signés ({toFollowup.length})
                </h3>
                {toFollowup.length === 0 ? (
                  <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>Aucun devis en attente</p>
                ) : toFollowup.slice(0, 5).map((q, i) => (
                  <div key={`col-${i}`} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid rgba(42,42,42,0.3)' }}>
                    <div>
                      <p className="text-xs font-semibold" style={{ color: 'var(--admin-text)' }}>{q.numero}</p>
                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{q.client_email} — {q.jours_attente}j</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="price-gold text-xs">{q.total?.toFixed(0)}€</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Clients inactifs */}
              <div className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: '#F9C5D1' }}>
                  <Users size={14} /> Clients à réactiver ({toReactivate.length})
                </h3>
                {toReactivate.length === 0 ? (
                  <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>Tous les clients sont actifs</p>
                ) : toReactivate.slice(0, 5).map((c, i) => (
                  <div key={`col-${i}`} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid rgba(42,42,42,0.3)' }}>
                    <div>
                      <p className="text-xs font-semibold" style={{ color: 'var(--admin-text)' }}>{c.nom || c.email}</p>
                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Inactif depuis {c.jours_inactif === 999 ? 'toujours' : `${c.jours_inactif}j`}</p>
                    </div>
                    <button
                      onClick={() => sendReactivation(c.client_id)}
                      disabled={sendingReactivation === c.client_id}
                      className="px-3 py-1 rounded-lg text-xs"
                      style={{ background: 'rgba(249,197,209,0.12)', color: '#F9C5D1' }}>
                      {sendingReactivation === c.client_id ? 'Envoi...' : 'Relancer'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* LOGS */}
          {activeTab === 'logs' && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>{logs.length} automatisations exécutées</p>
                <button onClick={fetchLogs} className="p-2 rounded-lg" style={{ background: 'var(--admin-surface)', color: 'var(--admin-gold)' }}><RefreshCw size={14} /></button>
              </div>
              {logs.length === 0 ? (
                <div className="p-8 rounded-xl text-center" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
                  <p className="text-sm" style={{ color: 'var(--admin-faint)' }}>Aucune automatisation exécutée pour l’instant</p>
                </div>
              ) : logs.map((log, i) => (
                <div key={log.id} className="flex items-start gap-3 p-3 rounded-xl mb-2" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
                  <CheckCircle size={14} style={{ color: '#3DDC97', flexShrink: 0, marginTop: '2px' }} />
                  <div className="flex-1">
                    <p className="text-xs font-semibold" style={{ color: 'var(--admin-text)' }}>{log.scenario}</p>
                    <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{log.client_email} — {log.triggered_at?.slice(0, 16)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
